import { cliente } from '@/lib/cliente';
import type { GenerarArchivoContadoSchema, PagarContadoAManoSchema } from '@adelante/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';

export function useListasContado(habilitada = true) {
  return useQuery({
    queryKey: ['listas-contado'],
    queryFn: () => cliente.listasContado.listar(),
    enabled: habilitada,
    refetchOnWindowFocus: true,
  });
}

export function useListaContado(id: number | null) {
  return useQuery({
    queryKey: ['listas-contado', id],
    queryFn: () => cliente.listasContado.detalle(id as number),
    enabled: id !== null,
    refetchOnWindowFocus: true,
  });
}

/** Lo que entraría al archivo de una cuenta de origen con las solicitudes marcadas (no escribe nada). */
export function useVistaPagoContado(
  id: number,
  cuenta: string | null,
  ids: number[],
  habilitada: boolean,
  cuentasElegidas: Record<string, number> = {},
) {
  return useQuery({
    queryKey: ['listas-contado', id, 'vista-previa', cuenta, ids, cuentasElegidas],
    queryFn: () => cliente.listasContado.vistaPrevia(id, cuenta as string, ids, cuentasElegidas),
    enabled: habilitada && !!cuenta && ids.length > 0,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** Cualquier cambio en una lista toca también las solicitudes (estado, lista, archivo). */
export function useAccionesListaContado() {
  const queryClient = useQueryClient();
  const refrescar = () => {
    queryClient.invalidateQueries({ queryKey: ['listas-contado'] });
    queryClient.invalidateQueries({ queryKey: ['solicitudes-contado'] });
  };
  return {
    cerrar: useMutation({
      mutationFn: (id: number) => cliente.listasContado.cerrar(id),
      onSuccess: refrescar,
    }),
    reabrir: useMutation({
      mutationFn: (id: number) => cliente.listasContado.reabrir(id),
      onSuccess: refrescar,
    }),
    renombrar: useMutation({
      mutationFn: ({ id, nombre }: { id: number; nombre: string }) =>
        cliente.listasContado.renombrar(id, nombre),
      onSuccess: refrescar,
    }),
    eliminar: useMutation({
      mutationFn: (id: number) => cliente.listasContado.eliminar(id),
      onSuccess: refrescar,
    }),
    generar: useMutation({
      mutationFn: ({
        id,
        datos,
      }: { id: number; datos: z.infer<typeof GenerarArchivoContadoSchema> }) =>
        cliente.listasContado.generar(id, datos),
      onSuccess: refrescar,
    }),
    marcarPagada: useMutation({
      mutationFn: ({
        id,
        referencia,
        fechaPago,
      }: { id: number; referencia: string | null; fechaPago?: string }) =>
        cliente.listasContado.marcarPagada(id, referencia, fechaPago),
      onSuccess: refrescar,
    }),
    cambiarFechaPago: useMutation({
      mutationFn: ({ idArchivo, fecha }: { idArchivo: number; fecha: string }) =>
        cliente.listasContado.cambiarFechaPago(idArchivo, fecha),
      onSuccess: refrescar,
    }),
    pagarAMano: useMutation({
      mutationFn: ({ id, datos }: { id: number; datos: z.infer<typeof PagarContadoAManoSchema> }) =>
        cliente.listasContado.pagarAMano(id, datos),
      onSuccess: refrescar,
    }),
    marcarPagado: useMutation({
      mutationFn: (idArchivo: number) => cliente.listasContado.marcarPagado(idArchivo),
      onSuccess: refrescar,
    }),
    deshacer: useMutation({
      mutationFn: (idArchivo: number) => cliente.listasContado.deshacer(idArchivo),
      onSuccess: refrescar,
    }),
  };
}
